import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { currentLocaleTag } from '../lib/formatting'
import {
  AlertCircle,
  CheckCircle2,
  CheckSquare,
  Circle,
  Clock,
  Download,
  ExternalLink,
  Loader2,
  Plus,
  Trash2,
  User as UserIcon,
} from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { confirmDialog, toast } from '../lib/dialogs'
import { downloadFile } from '../lib/download'
import TaskFormModal from '../components/tasks/TaskFormModal'
import { useAuth } from '../contexts/AuthContext'
import {
  type TaskDto,
  type TaskListResponse,
  type TaskPriorityT,
  type TaskStatusT,
} from '@toga/shared'

type Tab = 'mine' | 'all' | 'pending' | 'completed'

const PRIORITY_STYLES: Record<TaskPriorityT, string> = {
  BAJA: 'border-slate-500/30 text-slate-600 dark:text-slate-300 bg-slate-500/10',
  MEDIA: 'border-blue-500/40 text-blue-700 dark:text-blue-300 bg-blue-500/10',
  ALTA: 'border-amber-500/40 text-amber-700 dark:text-amber-300 bg-amber-500/10',
  URGENTE: 'border-rose-500/40 text-rose-700 dark:text-rose-200 bg-rose-500/15',
}

export default function Tasks() {
  const { t: tr } = useTranslation(['tasks', 'common'])
  const auth = useAuth()
  const canWrite =
    auth.status === 'authenticated' && auth.user.role !== 'LECTURA'
  const [tab, setTab] = useState<Tab>('mine')
  const [items, setItems] = useState<TaskDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<TaskDto | 'new' | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (tab === 'mine') params.set('mine', 'true')
      if (tab === 'pending') params.set('status', 'PENDIENTE')
      if (tab === 'completed') params.set('status', 'COMPLETADA')
      const resp = await api.get<TaskListResponse>(`/tasks?${params.toString()}`)
      setItems(resp.items)
    } catch (err) {
      setError(err instanceof Error ? err.message : tr('errors.load'))
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [tab, tr])

  useEffect(() => {
    void load()
  }, [load])

  const toggleComplete = async (t: TaskDto) => {
    try {
      if (t.status === 'COMPLETADA') {
        await api.patch(`/tasks/${t.id}`, { status: 'PENDIENTE' })
      } else {
        await api.post(`/tasks/${t.id}/complete`, {})
      }
      await load()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : tr('errors.generic'))
    }
  }

  const onDelete = async (t: TaskDto) => {
    if (!(await confirmDialog({ message: tr('confirmDelete', { title: t.title }), tone: 'danger' })))
      return
    try {
      await api.delete(`/tasks/${t.id}`)
      await load()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      toast.error(body?.error ?? tr('errors.generic'))
    }
  }

  const [exporting, setExporting] = useState<'tasks' | 'time' | null>(null)
  const onExportTime = async () => {
    setExporting('time')
    setError(null)
    try {
      const params = new URLSearchParams()
      if (tab === 'mine' && auth.status === 'authenticated') params.set('userId', auth.user.id)
      const qs = params.toString()
      await downloadFile(`/export/time-entries.csv${qs ? `?${qs}` : ''}`, 'tiempos.csv')
    } catch (err) {
      setError(err instanceof Error ? err.message : tr('errors.export'))
    } finally {
      setExporting(null)
    }
  }

  const grouped = useMemo(() => {
    // Agrupamos por status si estamos en "mine" o "all"; si filtramos, lista plana.
    if (tab === 'pending' || tab === 'completed') return [{ key: tab, items }]
    const order: TaskStatusT[] = ['PENDIENTE', 'EN_PROGRESO', 'COMPLETADA', 'CANCELADA']
    return order
      .map((status) => ({
        key: status,
        items: items.filter((i) => i.status === status),
      }))
      .filter((g) => g.items.length > 0)
  }, [items, tab])

  return (
    <div className="space-y-4 animate-slideUp">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-serif font-semibold t-primary flex items-center gap-2">
            <CheckSquare size={20} className="text-toga-700 dark:text-toga-300" />
            {tr('title')}
          </h1>
          <p className="text-xs t-muted">{items.length} {items.length === 1 ? tr('countOne') : tr('countOther')}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void onExportTime()}
            disabled={exporting === 'time'}
            title={tr('exportTimeTitle')}
            className="px-3 py-1.5 rounded-lg text-xs border border-base t-secondary hover:bg-hover hover:text-toga-700 dark:hover:text-toga-200 flex items-center gap-1.5 disabled:opacity-50"
          >
            {exporting === 'time' ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Download size={12} />
            )}
            {tr('exportTime')}
          </button>
          {canWrite && (
            <button
              type="button"
              onClick={() => setEditing('new')}
              className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 hover:shadow-glow-toga flex items-center gap-1.5"
            >
              <Plus size={14} /> {tr('newTask')}
            </button>
          )}
        </div>
      </header>

      {/* Tabs */}
      <div className="inline-flex rounded-lg border border-base overflow-hidden">
        {(
          [
            { k: 'mine' as const, l: tr('tabs.mine') },
            { k: 'all' as const, l: tr('tabs.all') },
            { k: 'pending' as const, l: tr('tabs.pending') },
            { k: 'completed' as const, l: tr('tabs.completed') },
          ]
        ).map((t, i) => (
          <button
            key={t.k}
            type="button"
            onClick={() => setTab(t.k)}
            className={[
              'px-3 py-1.5 text-xs',
              i > 0 ? 'border-l border-base' : '',
              tab === t.k ? 'bg-toga-500/15 text-toga-700 dark:text-toga-200' : 't-secondary hover:bg-hover',
            ].join(' ')}
          >
            {t.l}
          </button>
        ))}
      </div>

      {error && (
        <div className="surface p-3 text-sm rounded-lg border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {loading ? (
        <div className="t-muted flex items-center gap-2 text-sm">
          <Loader2 size={14} className="animate-spin" /> {tr('loading')}
        </div>
      ) : items.length === 0 ? (
        <div className="surface p-10 text-center">
          <CheckSquare size={32} className="mx-auto t-faint mb-2" />
          <p className="t-muted text-sm">
            {tab === 'mine'
              ? tr('empty.mine')
              : tr('empty.other')}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {grouped.map((group) => (
            <section key={group.key} className="surface p-4">
              <h3 className="t-primary font-semibold text-sm mb-2">
                {typeof group.key === 'string' && group.key !== 'pending' && group.key !== 'completed'
                  ? tr('enums:TaskStatus.' + (group.key as TaskStatusT))
                  : ''}
                <span className="text-xs t-faint font-normal ml-2">({group.items.length})</span>
              </h3>
              <ul className="divide-y divide-base/40">
                {group.items.map((t) => (
                  <li key={t.id} className="py-2 flex items-start gap-3">
                    {canWrite ? (
                      <button
                        type="button"
                        onClick={() => void toggleComplete(t)}
                        className="mt-0.5 t-muted hover:text-toga-700 dark:hover:text-toga-200"
                        title={
                          t.status === 'COMPLETADA' ? tr('markPending') : tr('markCompleted')
                        }
                      >
                        {t.status === 'COMPLETADA' ? (
                          <CheckCircle2 size={16} className="text-emerald-700 dark:text-emerald-300" />
                        ) : (
                          <Circle size={16} />
                        )}
                      </button>
                    ) : (
                      <span className="mt-0.5 t-faint">
                        {t.status === 'COMPLETADA' ? (
                          <CheckCircle2 size={16} className="text-emerald-700 dark:text-emerald-300" />
                        ) : (
                          <Circle size={16} />
                        )}
                      </span>
                    )}

                    {/* El título abre el editor; los chips van FUERA del botón:
                        un <a> anidado en un <button> es HTML inválido y el clic
                        al código acababa abriendo el editor en vez de navegar. */}
                    <div className="flex-1 min-w-0">
                      <button
                        type="button"
                        onClick={() => setEditing(t)}
                        className="w-full text-left"
                      >
                        <div
                          className={[
                            'text-sm',
                            t.status === 'COMPLETADA' || t.status === 'CANCELADA'
                              ? 't-faint line-through'
                              : 't-primary',
                          ].join(' ')}
                        >
                          {t.title}
                        </div>
                      </button>
                      <div className="text-[11px] t-muted flex items-center gap-2 flex-wrap mt-0.5">
                        <span className={`px-1.5 py-0.5 rounded border text-[10px] ${PRIORITY_STYLES[t.priority]}`}>
                          {tr('enums:TaskPriority.' + t.priority)}
                        </span>
                        {t.opportunityId && t.opportunityCode && (
                          <Link
                            to={`/opportunities/${t.opportunityId}`}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-toga-500/40 text-toga-700 dark:text-toga-300 hover:bg-toga-500/10 font-mono"
                            title={tr('openOpportunity')}
                          >
                            {t.opportunityCode} <ExternalLink size={9} />
                          </Link>
                        )}
                        {t.assignedToName && (
                          <span className="flex items-center gap-1">
                            <UserIcon size={10} /> {t.assignedToName}
                          </span>
                        )}
                        {t.dueAt && (
                          <DueDate iso={t.dueAt} done={t.status === 'COMPLETADA'} />
                        )}
                      </div>
                    </div>

                    {canWrite && (
                      <button
                        type="button"
                        onClick={() => void onDelete(t)}
                        className="w-7 h-7 grid place-items-center rounded hover:bg-rose-500/10 t-muted hover:text-rose-400"
                        title={tr('common:actions.delete')}
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      {editing && (
        <TaskFormModal
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null)
            await load()
          }}
        />
      )}
    </div>
  )
}

function DueDate({ iso, done }: { iso: string; done: boolean }) {
  const { t: tr } = useTranslation('tasks')
  const date = new Date(iso)
  const days = Math.ceil((date.getTime() - Date.now()) / (24 * 3600 * 1000))
  const overdue = !done && days < 0
  const soon = !done && days >= 0 && days <= 3
  const cls = done
    ? 't-faint'
    : overdue
      ? 'text-rose-700 dark:text-rose-300 font-semibold'
      : soon
        ? 'text-amber-700 dark:text-amber-300'
        : 't-secondary'
  return (
    <span className={`inline-flex items-center gap-1 ${cls}`}>
      <Clock size={10} /> {date.toLocaleDateString(currentLocaleTag())}
      {!done && (overdue ? ` ${tr('due.overdue', { days: Math.abs(days) })}` : days === 0 ? ` ${tr('due.today')}` : '')}
    </span>
  )
}
